/**
 * SimpleChatLogic - Enhanced WebSocket with Auto-Reconnection
 *
 * Based on working Python WebSocket test with added resilience:
 * - Automatic reconnection with exponential backoff
 * - Token refresh on reconnection
 * - Proactive reconnection before AWS timeout (10 minutes)
 * - Connection health monitoring
 * - Proper cleanup on intentional disconnects
 */

import { fetchAuthSession } from "aws-amplify/auth";
import { getWebSocketConfig } from "../config/config";

class SimpleChatLogic {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.connectionId = null;
    this.debugMode = process.env.NODE_ENV === "development";
    this.currentAgent = null; // Agent context for multi-agent support

    // Event listeners (compatible with original simpleWebSocketChat API)
    this.eventListeners = {
      thinking: [],
      chunk: [],
      complete: [],
      error: [],
      disconnected: [],
      reconnecting: [],
      reconnected: [],
      reconnect_failed: [],
    };

    // Reconnection management
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1000; // Start at 1 second
    this.maxReconnectDelay = 30000; // Cap at 30 seconds
    this.reconnectTimer = null;
    this.intentionalDisconnect = false;

    // Token management
    this.currentToken = null;
    this.tokenExpiry = null;

    // Connection health monitoring
    this.lastActivity = Date.now();
    this.healthCheckInterval = null;
    this.healthCheckFrequency = 60000; // Check every 60 seconds
    this.idleThreshold = 540000; // 9 minutes (before 10min AWS timeout)

    // CRITICAL FIX #3: Connection promise to prevent race conditions
    this.connectionPromise = null;
  }

  /**
   * Get ACCESS token (Amplify handles automatic refresh)
   */
  async getAccessToken() {
    try {
      const session = await fetchAuthSession();
      if (!session?.tokens?.accessToken?.toString) {
        throw new Error("No access token found");
      }
      const token = session.tokens.accessToken.toString();

      // Extract token expiry for proactive refresh
      this.tokenExpiry = this.extractTokenExpiry(token);

      return token;
    } catch (error) {
      console.error("❌ Failed to get access token:", error);
      throw error;
    }
  }

  /**
   * Extract token expiry timestamp from JWT
   */
  extractTokenExpiry(token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp; // Unix timestamp in seconds
    } catch (error) {
      console.warn("⚠️ Could not extract token expiry:", error);
      return null;
    }
  }

  /**
   * Check if token is expiring soon (< 5 minutes remaining)
   */
  isTokenExpiringSoon() {
    if (!this.tokenExpiry) return false;
    const now = Date.now() / 1000; // Convert to seconds
    const timeUntilExpiry = this.tokenExpiry - now;
    return timeUntilExpiry < 300; // Less than 5 minutes
  }

  /**
   * Connect to WebSocket with token refresh support
   * CRITICAL FIX: Prevent race conditions with connection promise
   */
  async connect(forceNewToken = false) {
    // CRITICAL FIX #3: Return existing connection promise if connection in progress
    if (this.connectionPromise) {
      console.log("⏸️ Connection already in progress, waiting...");
      return this.connectionPromise;
    }

    if (this.isConnected) {
      console.log("✅ Already connected");
      return Promise.resolve();
    }

    // Store the connection promise to prevent race conditions
    this.connectionPromise = this._connect(forceNewToken);

    try {
      await this.connectionPromise;
    } finally {
      this.connectionPromise = null;
    }
  }

  /**
   * Internal connection method
   */
  async _connect(forceNewToken) {
    try {
      // Get fresh token if forced or token is expiring soon
      if (forceNewToken || !this.currentToken || this.isTokenExpiringSoon()) {
        this.currentToken = await this.getAccessToken();
        if (this.debugMode) {
          console.log("🔑 Fresh token obtained");
        }
      }

      // MINOR FIX #7: Use config for WebSocket URL instead of hardcoding
      const websocketConfig = getWebSocketConfig();
      const websocketUrl = `${websocketConfig.url}?token=${this.currentToken}`;

      this.connectionId = `js_${Date.now()}`;

      if (this.debugMode) {
        console.log("🔌 Connecting to WebSocket...");
        console.log("🔍 Token length:", this.currentToken.length);
      }

      this.ws = new WebSocket(websocketUrl);

      return new Promise((resolve, reject) => {
        // IMPORTANT FIX #6: Store timeout ID for cleanup
        const timeoutId = setTimeout(() => {
          if (!this.isConnected && this.ws) {
            this.ws.close(); // Close the hanging connection
            reject(new Error("Connection timeout"));
          }
        }, 10000);

        this.ws.onopen = () => {
          clearTimeout(timeoutId); // Clear timeout on success
          this.isConnected = true;
          this.intentionalDisconnect = false;
          this.reconnectAttempts = 0; // Reset on successful connection
          this.lastActivity = Date.now();

          // Start health monitoring
          this.startHealthCheck();

          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onclose = (event) => {
          clearTimeout(timeoutId); // Clear timeout on close
          this.handleConnectionClose(event);
        };

        this.ws.onerror = (error) => {
          clearTimeout(timeoutId); // Clear timeout on error
          console.error("❌ WebSocket error:", error);
          this.emit("error", { message: "WebSocket connection error", error });
          reject(error);
        };
      });
    } catch (error) {
      console.error("❌ Connection failed:", error);
      throw error;
    }
  }

  /**
   * Handle WebSocket close event with auto-reconnection
   */
  handleConnectionClose(event) {
    this.isConnected = false;
    this.stopHealthCheck();

    // Emit disconnection event
    this.emit("disconnected", {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean,
    });

    // Don't reconnect if disconnect was intentional (logout, session change)
    if (this.intentionalDisconnect) {
      return;
    }

    // Auto-reconnect with exponential backoff
    this.scheduleReconnection();
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  scheduleReconnection() {
    // Check if we've exceeded max attempts
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("❌ Max reconnection attempts reached");
      this.emit("reconnect_failed", {
        attempts: this.reconnectAttempts,
        message: "Could not reconnect after multiple attempts",
      });
      return;
    }

    // Clear any existing reconnection timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;

    // Calculate delay with exponential backoff
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay,
    );

    console.log(
      `🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
    );

    this.emit("reconnecting", {
      attempt: this.reconnectAttempts,
      maxAttempts: this.maxReconnectAttempts,
      delay: delay,
    });

    this.reconnectTimer = setTimeout(async () => {
      try {
        console.log(
          `🔄 Attempting reconnection (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`,
        );
        await this.connect(true); // Force fresh token on reconnect
        console.log("✅ Reconnection successful!");
        this.emit("reconnected", { attempts: this.reconnectAttempts });
      } catch (error) {
        console.error("❌ Reconnection attempt failed:", error);

        // CRITICAL FIX #2: Manually schedule next attempt since onclose won't be called
        // if connect() throws before WebSocket is created
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnection();
        } else {
          this.emit("reconnect_failed", {
            attempts: this.reconnectAttempts,
            message: "Could not reconnect after multiple attempts",
          });
        }
      }
    }, delay);
  }

  /**
   * Start connection health monitoring
   */
  startHealthCheck() {
    this.stopHealthCheck();

    this.healthCheckInterval = setInterval(() => {
      const idleTime = Date.now() - this.lastActivity;

      // Proactive reconnection before AWS 10-minute timeout
      if (idleTime > this.idleThreshold && this.isConnected) {
        console.log(
          "⚠️ Connection idle for 9+ minutes, proactive reconnection",
        );
        this.reconnect();
        return;
      }

      // Check if token is expiring soon
      if (this.isTokenExpiringSoon() && this.isConnected) {
        console.log("🔑 Token expiring soon, reconnecting with fresh token");
        this.reconnect();
        return;
      }
    }, this.healthCheckFrequency);
  }

  /**
   * Stop health monitoring
   */
  stopHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  /**
   * Proactive reconnection (refresh connection)
   * CRITICAL FIX: Preserve event listeners during reconnection
   */
  async reconnect() {
    console.log("🔄 Initiating proactive reconnection...");
    this.intentionalDisconnect = true;

    // CRITICAL FIX #1: Store event listeners before disconnecting
    const savedListeners = {};
    Object.keys(this.eventListeners).forEach((key) => {
      savedListeners[key] = [...this.eventListeners[key]];
    });

    // Clean disconnect without clearing listeners
    this.cleanDisconnect();

    this.intentionalDisconnect = false;

    try {
      await this.connect(true);

      // Restore event listeners after successful reconnection
      this.eventListeners = savedListeners;

      console.log("✅ Proactive reconnection successful");
    } catch (error) {
      // Restore listeners even on failure
      this.eventListeners = savedListeners;
      console.error("❌ Proactive reconnection failed:", error);
      // Auto-reconnection logic will kick in from handleConnectionClose
    }
  }

  /**
   * Clean disconnect without clearing event listeners
   * Used for proactive reconnection to preserve UI handlers
   */
  cleanDisconnect() {
    // Clear reconnection timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Stop health monitoring
    this.stopHealthCheck();

    // Close WebSocket connection
    const ws = this.ws;
    if (ws) {
      // Clear handlers before closing to prevent reconnection
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;

      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        try {
          ws.close(1000, "Client initiated disconnect");
        } catch (error) {
          console.warn("⚠️ Error closing WebSocket:", error);
        }
      }

      this.ws = null;
    }

    this.isConnected = false;
    this.connectionId = null;
    this.reconnectAttempts = 0;

    // DON'T clear event listeners here - that's the whole point
  }

  /**
   * Send message with activity tracking
   */
  /**
   * Set the current agent context for messages
   * @param {Object} agent - Agent configuration object with key, id, alias
   */
  setAgentContext(agent) {
    this.currentAgent = agent;
    if (this.debugMode) {
      console.log("🤖 Agent context set:", agent?.key);
    }
  }

  /**
   * Clear the current agent context
   */
  clearAgentContext() {
    this.currentAgent = null;
    if (this.debugMode) {
      console.log("🗑️ Agent context cleared");
    }
  }

  /**
   * Send message with agent context
   * @param {string} message - The message to send
   * @param {string} sessionId - The session ID
   * @param {Object} agentOverride - Optional agent to use instead of current context
   */
  async sendMessage(message, sessionId, agentOverride = null, userContext = null) {
    if (!this.isConnected || !this.ws) {
      throw new Error("WebSocket not connected");
    }

    // Update activity timestamp
    this.lastActivity = Date.now();

    // Use override agent or current agent context
    const agent = agentOverride || this.currentAgent;

    if (!agent) {
      throw new Error("No agent selected. Cannot send message.");
    }

    // Payload with agent parameters for V4 handler
    // userContext carries only non-PII hints (userType, location, hasServiceHistory).
    // Sensitive fields (conditions, claimStage, familyStatus) are fetched server-side.
    const payload = {
      action: "sendMessage",
      message: message.trim(),
      sessionId: sessionId,
      agentKey: agent.key,
      agentId: agent.id,
      agentAlias: agent.alias,
    };

    if (userContext) {
      payload.userContext = userContext;
    }

    if (this.debugMode) {
      console.log("📤 Sending message payload with agent context:", payload);
    }

    this.ws.send(JSON.stringify(payload));
  }

  /**
   * Handle WebSocket messages with activity tracking
   */
  handleMessage(rawData) {
    // Update activity timestamp on any message
    this.lastActivity = Date.now();

    try {
      if (this.debugMode) {
        console.log("📨 Raw message received:", rawData);
      }

      const data = JSON.parse(rawData);

      if (this.debugMode) {
        console.log("📨 Parsed message:", data);
      }

      // Skip warmup session events (same filter as current code)
      if (data.session_id && data.session_id.startsWith("warmup-")) {
        console.log("🔇 Skipping warmup session event");
        return;
      }

      // Handle message types (exact Python logic)
      switch (data.type) {
        case "typing":
        case "thinking":
          this.emit("thinking", data);
          break;

        case "chunk":
          // Enhanced chunk debugging
          if (this.debugMode) {
            console.log("📝 Chunk details:", {
              content: data.content,
              contentLength: data.content?.length,
              chunkIndex: data.chunkIndex,
              hasContent: !!data.content,
              startsWithQuote: data.content?.startsWith('"'),
              endsWithBrace: data.content?.endsWith("}"),
              endsWithQuoteBrace: data.content?.endsWith('"}'),
            });
          }
          this.emit("chunk", data);
          break;

        case "complete":
          // Exact Python completion logic
          this.emit("complete", data);
          break;

        case "error":
          this.emit("error", data);
          break;

        default:
          // Handle only actual errors, ignore timeout informational messages
          if (data.message) {
            if (data.message.includes("Internal server error")) {
              this.emit("error", {
                message:
                  "AI service temporarily unavailable. Please try again.",
                type: "server_error",
              });
            } else if (data.message.includes("timed out") || data.message.includes("timeout")) {
              // Timeout messages are informational - don't emit error, let streaming continue
              // Frontend auto-completion timeout (60s) will handle actual timeouts
              console.log("ℹ️ Backend sent timeout info message, continuing to wait for chunks...");
            } else {
              // Emit as general message for unknown types
              this.emit("message", data);
            }
          }
          break;
      }
    } catch (error) {
      console.error("❌ Failed to parse message:", error, rawData);
      this.emit("error", { message: "Failed to parse response" });
    }
  }

  /**
   * Add event listener (compatible with original simpleWebSocketChat API)
   */
  on(event, handler) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].push(handler);
    }
  }

  /**
   * Remove event listener (compatible with original simpleWebSocketChat API)
   */
  off(event, handler) {
    if (this.eventListeners[event]) {
      const index = this.eventListeners[event].indexOf(handler);
      if (index > -1) {
        this.eventListeners[event].splice(index, 1);
      }
    }
  }

  /**
   * Emit event to all listeners
   */
  emit(event, data) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in ${event} handler:`, error);
        }
      });
    }
  }

  /**
   * Disconnect and cleanup
   * IMPORTANT FIX #5: Accept intentional parameter instead of direct property mutation
   */
  disconnect(intentional = true) {
    const previousIntentional = this.intentionalDisconnect;
    this.intentionalDisconnect = intentional;

    // Clear reconnection timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Stop health monitoring
    this.stopHealthCheck();

    // MINOR FIX #9: Store reference to avoid race condition
    const ws = this.ws;
    if (ws) {
      // Clear handlers before closing to prevent reconnection
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;

      if (
        ws.readyState === WebSocket.OPEN ||
        ws.readyState === WebSocket.CONNECTING
      ) {
        try {
          ws.close(1000, "Client initiated disconnect");
        } catch (error) {
          console.warn("⚠️ Error closing WebSocket:", error);
        }
      }

      this.ws = null;
    }

    this.isConnected = false;
    this.connectionId = null;
    this.reconnectAttempts = 0;

    // CRITICAL: Clear all event listeners to prevent duplicates on reconnect
    this.eventListeners = {
      thinking: [],
      chunk: [],
      complete: [],
      error: [],
      disconnected: [],
      reconnecting: [],
      reconnected: [],
      reconnect_failed: [],
    };

    // Restore intentional flag if it wasn't set externally
    if (!previousIntentional) {
      this.intentionalDisconnect = false;
    }
  }

  /**
   * Get connection status (enhanced with reconnection info)
   */
  getStatus() {
    return {
      isConnected: this.isConnected,
      connectionId: this.connectionId,
      readyState: this.ws?.readyState,
      reconnectAttempts: this.reconnectAttempts,
      intentionalDisconnect: this.intentionalDisconnect,
      lastActivity: this.lastActivity,
      idleTime: Date.now() - this.lastActivity,
      tokenExpiry: this.tokenExpiry,
      tokenExpiringSoon: this.isTokenExpiringSoon(),
    };
  }

  /**
   * Manual reconnection (resets attempt counter)
   */
  async manualReconnect() {
    console.log("🔄 Manual reconnection requested");
    this.reconnectAttempts = 0; // Reset counter for manual reconnect

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Use improved disconnect method
    this.disconnect(true);

    await this.connect(true);
  }
}

// Create singleton instance to match original simpleWebSocketChat API
const simpleChatLogic = new SimpleChatLogic();

export default simpleChatLogic;
